import type { z } from 'zod';
import React from 'react';
import { Box } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import { Navigate, useParams } from 'react-router';

import { zAuthActionControllerCompleteInviteData } from '~/api/zod.gen';
import { AuthLayout } from '~/components/common';
import { LOGIN } from '~/constants/routes';
import { useLanguage } from '~/hooks/language';
import { useNextTick } from '~/hooks/next-tick';
import { useAppDispatch } from '~/hooks/store';
import { authAction as authActionApi } from '~/store/api/auth-action';
import { setAlertMessage } from '~/store/ux';
import { getErrorMessage } from '~/utils/api';

import { CreatePassword } from './CreatePassword';
import { SetupTwoFactorForm } from './SetupTwoFactorForm';
import { SetupTwoFactorQrCode } from './SetupTwoFactorQrCode';
import { SuccessView } from './SuccessView';

const inviteValidationSchema = zAuthActionControllerCompleteInviteData.shape.body
  .extend({
    confirmPassword: zAuthActionControllerCompleteInviteData.shape.body.shape.password,
  })
  .superRefine(({ confirmPassword, password }, ctx) => {
    if (confirmPassword !== password) {
      ctx.addIssue({
        code: 'custom',
        message: 'validations.passwordsMismatch',
        path: ['confirmPassword'],
      });
    }
  });

type InviteFormValues = z.infer<typeof inviteValidationSchema>;

const validate = (values: InviteFormValues) => {
  const result = inviteValidationSchema.safeParse(values);
  if (result.success) return {};
  return result.error.formErrors.fieldErrors;
};

export const Invite: React.FC = () => {
  const { translate } = useLanguage('ua');
  const [view, setView] = React.useState<'create-password' | 'two-factor-auth' | 'success'>('create-password');
  const params = useParams();
  const onNextTick = useNextTick();
  const dispatch = useAppDispatch();

  const { data } = authActionApi.endpoints.getInvite.useQuery({ id: Number(params.id ?? '-1') });
  const [getInviteQRCode, { data: qrCodeData, isFetching }] = authActionApi.endpoints.getInviteQRCode.useLazyQuery();
  const [inviteComplete] = authActionApi.endpoints.inviteComplete.useMutation();
  const qrCodeLink = qrCodeData?.success ? qrCodeData.data.qrCode : '';
  const inviteData = data?.success ? data.data : undefined;
  const isInviteAlreadyUsed = Boolean(inviteData?.completed);
  const isInviteExpired = Boolean(inviteData?.expired);

  const formikContext = useFormik<InviteFormValues>({
    enableReinitialize: true,
    validate,
    initialValues: {
      password: '',
      confirmPassword: '',
      code: '',
    },
    onSubmit: async values => {
      const result = await inviteComplete({
        body: { password: values.password, code: values.code },
        id: Number(params.id ?? '-1'),
      });

      onNextTick(() => {
        if ('data' in result) {
          if (result.data.success) {
            setView('success');
          }
        } else {
          const error = getErrorMessage(result.error);
          dispatch(
            setAlertMessage({
              severity: 'error',
              message: error === 'general.generalErrorMsg' ? translate(error) : error,
            })
          );
        }
      });
    },
  });

  const onCreatePasswordContinue = () => {
    setView('two-factor-auth');
  };

  React.useEffect(() => {
    if (!isInviteAlreadyUsed && !isInviteExpired) void getInviteQRCode({ id: Number(params.id ?? '-1') });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInviteAlreadyUsed, isInviteExpired]);

  if (isInviteAlreadyUsed || isInviteExpired) {
    console.info('isInviteAlreadyUsed || isInviteExpired', isInviteAlreadyUsed, isInviteExpired);
    return <Navigate to={LOGIN} replace />;
  }

  return (
    <AuthLayout>
      <FormikProvider value={formikContext}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Form style={{ width: '100%', flexShrink: 0 }}>
            {view === 'create-password' && (
              <CreatePassword
                password={formikContext.values.password}
                isSubmitDisabled={
                  !formikContext.dirty ||
                  Boolean(formikContext.errors.password) ||
                  Boolean(formikContext.errors.confirmPassword)
                }
                inviteeName={inviteData?.metadata.name ?? ''}
                onContinue={onCreatePasswordContinue}
              />
            )}

            {view === 'two-factor-auth' && (
              <>
                <SetupTwoFactorForm
                  isBusy={formikContext.isSubmitting}
                  status={formikContext.status as string | undefined}
                />
                <SetupTwoFactorQrCode qrCodeLink={qrCodeLink} isPending={isFetching} />
              </>
            )}

            {view === 'success' && <SuccessView />}
          </Form>
        </Box>
      </FormikProvider>
    </AuthLayout>
  );
};
